// EmailTemplatesPage — вкладка «Шаблоны» раздела «Почта» (V15 «Почта» W7, ТЗ §3.8).
// GET /api/email/templates (свои + системные — read-only, бейдж «Системный»);
// «Новый шаблон» → EmailTemplateDialog (POST 201); «Редактировать»/«Удалить» — только
// свои (системные/чужие → 403BE); дубль названия → 409 → Alert.
// Системный «Счёт (канон)» — из коробки (FR-10, V40 seed), read-only.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Chip, IconButton, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { emailApi, type EmailTemplate } from '@/api/email';
import { extractApiErrorMessage } from '@/lib/apiError';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { EmptyState } from '@/theme/EmptyState';
import { t } from '@/i18n';
import { EmailTemplateDialog } from './components/EmailTemplateDialog';

/** Вкладка шаблонов: список + диалог создания/редактирования. */
export function EmailTemplatesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<EmailTemplate | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['email', 'templates'],
    queryFn: () => emailApi.listTemplates(0, 100),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => emailApi.deleteTemplate(id),
    onSuccess: () => {
      setPageError(null);
      void queryClient.invalidateQueries({ queryKey: ['email', 'templates'] });
    },
    onError: (error) => {
      setPageError(extractApiErrorMessage(error, t('email.templates.saveError')));
    },
  });

  const openCreate = (): void => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (template: EmailTemplate): void => {
    setEditing(template);
    setDialogOpen(true);
  };

  if (query.isLoading) {
    return <LoadingSpinner label={t('common.loading')} />;
  }
  if (query.isError) {
    return <Alert severity="error">{query.error instanceof Error ? query.error.message : t('email.templates.loadError')}</Alert>;
  }
  const templates = query.data?.content ?? [];

  if (templates.length === 0) {
    return (
      <>
        <Stack spacing={2}>
          <EmptyState title={t('email.templates.empty')} description={t('email.templates.emptyDesc')} />
          <div>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate}>
              {t('email.templates.create')}
            </Button>
          </div>
        </Stack>
        <EmailTemplateDialog open={dialogOpen} onClose={() => setDialogOpen(false)} template={editing} />
      </>
    );
  }

  return (
    <>
      <Paper>
        {pageError ? <Alert severity="error" sx={{ m: 2 }}>{pageError}</Alert> : null}
        <Stack direction="row" justifyContent="flex-end" sx={{ p: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            {t('email.templates.create')}
          </Button>
        </Stack>
        <Table size="small" aria-label={t('email.tabs.templates')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('email.templates.name')}</TableCell>
              <TableCell>{t('email.templates.subject')}</TableCell>
              <TableCell>{t('email.templates.contentType')}</TableCell>
              <TableCell align="right">{t('invoices.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((template) => (
              <TableRow key={template.id} hover>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {template.name}
                    {template.isSystem ? <Chip size="small" label={t('email.templates.systemBadge')} color="default" /> : null}
                  </Stack>
                </TableCell>
                <TableCell>{template.subject}</TableCell>
                <TableCell>{t(template.contentType === 'HTML' ? 'email.templates.contentType.HTML' : 'email.templates.contentType.PLAIN')}</TableCell>
                <TableCell align="right">
                  {template.isSystem ? null : (
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title={t('common.edit')}>
                        <IconButton size="small" aria-label={t('common.edit')} onClick={() => openEdit(template)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete')}>
                        <IconButton
                          size="small"
                          aria-label={t('common.delete')}
                          disabled={deleteMutation.isPending}
                          onClick={() => deleteMutation.mutate(template.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <EmailTemplateDialog open={dialogOpen} onClose={() => setDialogOpen(false)} template={editing} />
    </>
  );
}
